import { useMemo, useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { useSEO, SchemaOrg } from '@/components/base/SEO';
import { aboutData, faqs } from '@/mocks/manga';
import { get_form_url } from '@/utils/get_form_url';

const SITE_URL = 'https://beilemanga.com';

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formUrl, setFormUrl] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadFormUrl() {
      try {
        const result = await get_form_url('贝勒漫画 联系我们');
        if (!cancelled && result?.submitAddr) {
          setFormUrl(result.submitAddr);
        }
      } catch {
        if (!cancelled) setFormError('表单加载失败，请稍后再试');
      }
    }
    loadFormUrl();
    return () => { cancelled = true; };
  }, []);

  useSEO({
    title: '关于贝勒漫画 - 品牌介绍与联系我们 | 贝勒漫画官网',
    description: '了解贝勒漫画网的品牌故事、发展历程和核心优势。我们致力于为漫画爱好者提供最优质的在线阅读体验，覆盖韩漫日漫国漫等多种类型。',
    keywords: '贝勒漫画, 关于贝勒漫画, 贝勒漫画品牌, 漫画平台, 联系我们, 漫画在线阅读',
    canonicalUrl: `${SITE_URL}/about`,
    ogType: 'website',
  });

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      return;
    }

    if (!formUrl) {
      setFormError('表单暂不可用，请稍后重试');
      return;
    }

    setFormSubmitting(true);
    try {
      const formData = new FormData(form);
      const response = await fetch(formUrl, {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
    } finally {
      setFormSubmitting(false);
    }
  }, [formUrl]);

  const stars = (rating: number) => {
    const filled = Array.from({ length: Math.floor(rating) });
    const empty = Array.from({ length: 5 - Math.floor(rating) });
    return (
      <span className="flex items-center gap-0.5">
        {filled.map((_, i) => (
          <i key={`f-${i}`} className="ri-star-fill text-accent-500 text-xs w-3 h-3 flex items-center justify-center"></i>
        ))}
        {empty.map((_, i) => (
          <i key={`e-${i}`} className="ri-star-line text-foreground-300 text-xs w-3 h-3 flex items-center justify-center"></i>
        ))}
      </span>
    );
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '贝勒漫画网',
    url: SITE_URL,
    description: '国内领先的漫画在线阅读平台',
    email: aboutData.contact.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutData.contact.address,
      addressCountry: 'CN',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: `${SITE_URL}/about` },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SchemaOrg data={organizationSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <SchemaOrg data={faqSchema} />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        {/* Hero */}
        <section className="bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-16 text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              关于 {aboutData.brandName}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {aboutData.brandIntro}
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutData.stats.map((stat, i) => (
              <div key={i} className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</div>
                <div className="text-sm text-foreground-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Mission */}
        <section className="bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">我们的使命</h2>
            </div>
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
                <i className="ri-heart-pulse-line text-2xl w-8 h-8 flex items-center justify-center"></i>
              </div>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed font-heading">
                "{aboutData.mission}"
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">平台特色</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {aboutData.features.map((feature, i) => (
              <div key={i} className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-50 text-primary-500 shrink-0">
                  <i className={`${feature.icon} w-5 h-5 flex items-center justify-center`}></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">{feature.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reviews */}
        <section className="bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">用户口碑</h2>
              <p className="text-sm text-foreground-600 mt-1">来自真实用户的评价</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {aboutData.reviews.map((review, i) => (
                <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 shrink-0">
                      <span className="text-xs font-bold">{review.user.slice(0, 2)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{review.user}</p>
                      <div className="flex items-center gap-1">
                        {stars(review.rating)}
                        <span className="text-xs text-foreground-500 ml-1">{review.date}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">联系我们</h2>
            <p className="text-sm text-foreground-600 mt-1">有任何问题或合作意向，欢迎联系我们</p>
          </div>
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-1/2 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-50 text-primary-500 shrink-0">
                  <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">邮箱</h3>
                  <p className="text-sm text-foreground-600">{aboutData.contact.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-500 shrink-0">
                  <i className="ri-qq-line w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">QQ群</h3>
                  <p className="text-sm text-foreground-600">{aboutData.contact.qq}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-500 shrink-0">
                  <i className="ri-wechat-line w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">微信</h3>
                  <p className="text-sm text-foreground-600">{aboutData.contact.wechat}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 shrink-0">
                  <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">地址</h3>
                  <p className="text-sm text-foreground-600">{aboutData.contact.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 shrink-0">
                  <i className="ri-time-line w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">工作时间</h3>
                  <p className="text-sm text-foreground-600">{aboutData.contact.businessHours}</p>
                </div>
              </div>
            </div>

            <div className="lg:w-1/2">
              {formSubmitted ? (
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-8 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
                    <i className="ri-check-line text-2xl w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-600">感谢您的留言，我们会在1-2个工作日内回复您。</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-background-50 border border-background-200/70 rounded-lg p-6" data-readdy-form>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                        placeholder="请输入姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                        placeholder="请输入邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="subject" className="block text-xs font-medium text-foreground-700 mb-1">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1">留言</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    />
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="anti-spam-field"
                  />
                  {formError && (
                    <p className="text-xs text-red-500 mb-3">{formError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full bg-primary-500 text-background-50 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formSubmitting ? '提交中...' : '提交留言'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">常见问题</h2>
              <p className="text-sm text-foreground-600 mt-1">快速解答您的疑问</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors duration-200">
                    <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.question}</span>
                    <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform duration-200 shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}